/**
 * Chart theming from tokens — design/09 §7.1–§7.4.
 *
 * Charts are the one consumer that cannot use a CSS custom property: ECharts
 * computes marks into a canvas and needs a resolved colour. So this module reads the
 * generated token constants for the ACTIVE theme, and it is the only sanctioned
 * place in the product where a token is resolved to a literal.
 *
 * §7.1: "All palettes below were validated against those exact surfaces, not against
 * generic white/black." The theme therefore never falls back to a generic default —
 * a missing token is a build error, not a grey.
 */

import { themes, type Theme } from '../tokens/generated/tokens';
import type { Series } from './types';

const token = (theme: Theme, name: string): string => {
  const value = themes[theme][name as keyof (typeof themes)['light']];
  if (value === undefined) {
    throw new Error(`chart theme: token "${name}" does not exist. Charts resolve tokens to literals and there is no fallback colour — add it to tokens/ and run npm run tokens:build.`);
  }
  return value;
};

export interface ChartTheme {
  readonly surface: string;
  readonly categorical: readonly string[];
  readonly sequential: (ramp: 'blue' | 'orange') => readonly string[];
  readonly diverging: readonly string[];
  readonly ordinal: readonly string[];
  readonly ink: { title: string; subtitle: string; axisLabel: string; annotation: string };
  readonly structure: {
    grid: string; axis: string; lineWidth: number; markerSize: number;
    fillGap: number; targetBand: string;
  };
  readonly missing: { notReported: string; notRanged: string };
  readonly font: { ui: string; numeric: string; display: string };
  readonly fontSize: { title: number; subtitle: number; axis: number; label: number };
}

const px = (v: string) => Number.parseFloat(v);

export function chartTheme(theme: Theme): ChartTheme {
  const t = (name: string) => token(theme, name);
  return {
    surface: t('color-surface-page'),
    // §7.2 — seven slots. Not eight.
    categorical: [1, 2, 3, 4, 5, 6, 7].map((i) => t(`chart-categorical-${i}`)),
    sequential: (ramp) => [50, 100, 250, 400, 500, 600, 700, 800, 900]
      .map((step) => t(`chart-sequential-${ramp}-${step}`)),
    // Index 0 is furthest below plan; the red arm means "below plan" in every chart.
    diverging: [0, 1, 2, 3, 4, 5, 6].map((i) => t(`chart-diverging-${i}`)),
    ordinal: (theme === 'light' ? [1, 2, 3, 4, 5, 6, 7] : [1, 2, 3, 4, 5, 6])
      .map((i) => t(`chart-ordinal-${theme}-${i}`)),
    ink: {
      // §7.4 — "Text always wears ink tokens, never the series colour."
      title: t('chart-ink-title'),
      subtitle: t('chart-ink-subtitle'),
      axisLabel: t('chart-ink-axis-label'),
      annotation: t('chart-ink-annotation'),
    },
    structure: {
      grid: t('chart-structure-grid'),
      axis: t('chart-structure-axis'),
      lineWidth: px(t('chart-structure-line-width')),
      markerSize: px(t('chart-structure-marker-size')),
      fillGap: px(t('chart-structure-fill-gap')),
      targetBand: t('chart-structure-target-band'),
    },
    missing: {
      notReported: t('chart-missing-not-reported'),
      notRanged: t('chart-missing-not-ranged'),
    },
    font: {
      ui: t('font-family-ui'),
      numeric: t('font-family-numeric'),
      display: t('font-family-display'),
    },
    fontSize: {
      title: px(t('type-subsection-size')),
      subtitle: px(t('type-caption-size')),
      // §4.3 — every number in an axis tick is IBM Plex Mono at the numeric size.
      axis: px(t('type-numeric-size')),
      label: px(t('type-label-size')),
    },
  };
}

/**
 * §2.5: "Colour assigned by rank" is banned — "filtering a store list must not
 * repaint the survivors — colour follows the entity."
 *
 * So the slot is a pure function of the series id, never of its index. Two charts of
 * the same store show it in the same colour, and removing a store from a filter
 * leaves the others where they were.
 */
export function slotForSeries(series: Series, allIds: readonly string[]): number {
  if (series.slot !== undefined) return series.slot;
  // Stable across sessions and across filter states: hash the id, not the position.
  let hash = 0;
  for (let i = 0; i < series.id.length; i += 1) {
    hash = (hash * 31 + series.id.charCodeAt(i)) | 0;
  }
  const preferred = (Math.abs(hash) % 7) + 1;
  // Resolve a collision deterministically by walking forward, so the assignment is
  // still independent of render order for the surviving entities.
  const taken = new Set<number>();
  for (const id of [...allIds].sort()) {
    if (id === series.id) break;
    let h = 0;
    for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) | 0;
    taken.add((Math.abs(h) % 7) + 1);
  }
  let slot = preferred;
  for (let attempt = 0; attempt < 7 && taken.has(slot); attempt += 1) slot = (slot % 7) + 1;
  return slot;
}

export const markerSymbol: Record<string, string> = {
  'marker-circle': 'circle',
  'marker-square': 'rect',
  'marker-triangle': 'triangle',
  'marker-diamond': 'diamond',
  'pattern-solid': 'circle',
  'pattern-hatch': 'circle',
  'pattern-dot': 'circle',
  'direct-label': 'circle',
};
